import { PermissionAction } from "../../flatbuffers/generated/archive/permission-action";
import formatUuidT from "../../flatbuffers/display/uuid";
import type { UuidT } from "../../flatbuffers/generated/front-end/uuid";
import type { RoleScopeGrants, ScopeGrant } from "./scopeSlice";

/**
 * Extract role scope grants from a /users/me payload.
 *
 * Tolerant of two shapes:
 *   1. FlatBuffer *T camelCase: { bankPermissions: RoleBankPermissionT[], ... }
 *   2. JSON snake_case:          { bank_permissions: [...], ... }
 *
 * In both shapes, each grant item has:
 *   - entityId field: bankId / liftId / bankFloorId (camelCase FB) or
 *                     bank_id / lift_id / bank_floor_id (snake_case JSON)
 *   - action field: PermissionAction enum value (number) in either shape
 *
 * Maps action via the PermissionAction enum — never hand-rolled map.
 * Returns empty arrays (allow-all) when the vectors are absent.
 */

type AnyGrant = Record<string, unknown>;

function extractUuidString(grant: AnyGrant, keys: string[]): string {
  const found = keys.find((key) => {
    const v = grant[key];
    return v !== null && v !== undefined && v !== "";
  });
  if (!found) return "";
  const v = grant[found];
  if (typeof v === "string") return v;
  // FlatBuffer UuidT shape — use formatUuidT which handles UuidT and POJOs
  return formatUuidT(v as UuidT | string | number | null | undefined) || "";
}

function toPermissionAction(raw: unknown): PermissionAction {
  const n = typeof raw === "number" ? raw : Number(raw);
  // Validate against known enum values (0-4). Unknown values fall back to VIEW.
  if (n === PermissionAction.VIEW) return PermissionAction.VIEW;
  if (n === PermissionAction.CREATE) return PermissionAction.CREATE;
  if (n === PermissionAction.UPDATE) return PermissionAction.UPDATE;
  if (n === PermissionAction.DELETE) return PermissionAction.DELETE;
  if (n === PermissionAction.ACTION) return PermissionAction.ACTION;
  return PermissionAction.VIEW;
}

function extractGrants(items: AnyGrant[], camelKey: string, snakeKey: string): ScopeGrant[] {
  return items.reduce<ScopeGrant[]>((acc, item) => {
    const entityId = extractUuidString(item, [camelKey, snakeKey]);
    if (entityId) {
      acc.push({ entityId, action: toPermissionAction(item.action) });
    }
    return acc;
  }, []);
}

function asGrantArray(v: unknown): AnyGrant[] {
  if (!Array.isArray(v)) return [];
  return v as AnyGrant[];
}

function resolveRoleField(
  u: Record<string, unknown>,
  camelKey: string,
  snakeKey: string,
): AnyGrant[] {
  const topCamel = asGrantArray(u[camelKey]);
  if (topCamel.length > 0) return topCamel;
  const topSnake = asGrantArray(u[snakeKey]);
  if (topSnake.length > 0) return topSnake;
  // Fall back to nested role object for legacy shapes.
  const role = u.role as Record<string, unknown> | null | undefined;
  if (!role) return [];
  return asGrantArray(role[camelKey] ?? role[snakeKey]);
}

/**
 * Extract scope grants from a user payload object.
 *
 * The payload is the raw response from /users/me — which may have the
 * permission vectors at the top level (User table carries them per B1)
 * or nested inside a `role` sub-object, depending on the API shape.
 */
export function extractRoleScopeGrants(userPayload: unknown): RoleScopeGrants {
  const empty: RoleScopeGrants = {
    bankGrants: [],
    liftGrants: [],
    floorGrants: [],
  };

  if (!userPayload || typeof userPayload !== "object") return empty;

  const u = userPayload as Record<string, unknown>;

  return {
    bankGrants: extractGrants(
      resolveRoleField(u, "bankPermissions", "bank_permissions"),
      "bankId",
      "bank_id",
    ),
    liftGrants: extractGrants(
      resolveRoleField(u, "liftPermissions", "lift_permissions"),
      "liftId",
      "lift_id",
    ),
    floorGrants: extractGrants(
      resolveRoleField(u, "floorPermissions", "floor_permissions"),
      "bankFloorId",
      "bank_floor_id",
    ),
  };
}

export default extractRoleScopeGrants;
